import { useId, type ReactElement, type ReactNode } from 'react';

/**
 * Labelled form fields (brand: label above, error in words below, never colour alone). Every field has a visible
 * label tied to its control, and its error is announced through aria-describedby.
 *
 * Widths (docs/specs/ui-layout-rules.md rule 3): a box is as wide as what it holds, in steps of the `field-*` tokens.
 * By default a list is 320 px (480 px when a choice is long), a date or time 160 px, other boxes their column; on a
 * phone or an iPad in portrait every box is the full width (styles.css, "ui-fix-fields").
 */
export type FieldWidth = 'xs' | 's' | 'm' | 'l' | 'full';

interface Base {
  readonly label: string;
  readonly error?: string | undefined;
  readonly hint?: string | undefined;
  readonly disabled?: boolean | undefined;
  readonly className?: string | undefined;
  /** The box's width step (rule 3): xs 96 px, s 160 px, m 320 px, l 480 px, full its whole column. */
  readonly width?: FieldWidth | undefined;
}

function Wrap({
  id,
  label,
  error,
  hint,
  className,
  width,
  children,
}: Base & { readonly id: string; readonly children: ReactNode }): ReactElement {
  return (
    <div className={['field', width ? `field--${width}` : '', className].filter(Boolean).join(' ')}>
      <label className="label muted" htmlFor={id}>
        {label}
      </label>
      {children}
      {hint && (
        <p id={`${id}-hint`} className="caption muted field__hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="field__error caption" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

const described = (id: string, error?: string, hint?: string): string | undefined =>
  [error ? `${id}-error` : '', hint ? `${id}-hint` : ''].filter(Boolean).join(' ') || undefined;

/**
 * Choices longer than this many characters do not fit the 320 px list (about 270 px of words in the interface
 * font, its arrow and padding aside), so the list takes the 480 px step: a list is never narrower than its longest
 * choice (rule 3).
 */
const LIST_M_CHARS = 32;

/** The width step a list takes from its choices (rule 3): m, or l when a choice is long. */
export function listWidth(labels: readonly string[]): FieldWidth {
  return labels.some((l) => l.length > LIST_M_CHARS) ? 'l' : 'm';
}

export function TextField({
  value,
  onChange,
  type = 'text',
  autoComplete,
  autoFocus,
  spellCheck = true,
  ...base
}: Base & {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly type?: 'text' | 'email' | 'date' | 'month' | 'tel' | 'url' | 'password';
  readonly autoComplete?: string;
  /** Put the cursor here when the field appears (a form opened by a button). */
  readonly autoFocus?: boolean;
  /**
   * Spelling underlined as the person types, in Australian English (the page's `lang`), with right-click suggestions
   * and "Add to dictionary" (decisions 661 and 709). On by default: every field people type words into is checked,
   * names, places, addresses, titles and labels included. A field that holds no words turns it off
   * (`spellCheck={false}`): numbers, codes, registrations, versions, usernames, server addresses, folders, file names.
   * Never on for an email, password, date, month, phone or web address field, whatever is passed.
   */
  readonly spellCheck?: boolean;
}): ReactElement {
  const id = useId();
  return (
    <Wrap id={id} {...base}>
      <input
        id={id}
        className="input"
        type={type}
        value={value}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        spellCheck={type === 'text' && spellCheck}
        disabled={base.disabled}
        aria-invalid={base.error ? true : undefined}
        aria-describedby={described(id, base.error, base.hint)}
        onChange={(e) => onChange(e.target.value)}
      />
    </Wrap>
  );
}

/** A number or empty (null). `step` decides decimals. */
export function NumberField({
  value,
  onChange,
  step = 'any',
  unit,
  ...base
}: Base & {
  readonly value: number | null;
  readonly onChange: (v: number | null) => void;
  readonly step?: string;
  readonly unit?: string;
}): ReactElement {
  const id = useId();
  return (
    <Wrap id={id} {...base} label={unit ? `${base.label} (${unit})` : base.label}>
      <input
        id={id}
        className="input data"
        type="number"
        inputMode="decimal"
        step={step}
        value={value === null || Number.isNaN(value) ? '' : String(value)}
        disabled={base.disabled}
        aria-invalid={base.error ? true : undefined}
        aria-describedby={described(id, base.error, base.hint)}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
      />
    </Wrap>
  );
}

export function SelectField<T extends string>({
  value,
  onChange,
  options,
  ...base
}: Base & {
  readonly value: T;
  readonly onChange: (v: T) => void;
  readonly options: ReadonlyArray<readonly [T, string]>;
}): ReactElement {
  const id = useId();
  return (
    <Wrap id={id} {...base} width={base.width ?? listWidth(options.map(([, l]) => l))}>
      <select
        id={id}
        className="input"
        value={value}
        disabled={base.disabled}
        aria-invalid={base.error ? true : undefined}
        aria-describedby={described(id, base.error, base.hint)}
        onChange={(e) => onChange(e.target.value as T)}
      >
        {options.map(([v, l]) => (
          <option key={v} value={v}>
            {l}
          </option>
        ))}
      </select>
    </Wrap>
  );
}

export function TextArea({
  value,
  onChange,
  rows = 4,
  spellCheck = true,
  ...base
}: Base & {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly rows?: number;
  /** Spelling underlined as the person types (decisions 661 and 709): on by default; off only for one holding no words. */
  readonly spellCheck?: boolean;
}): ReactElement {
  const id = useId();
  return (
    <Wrap id={id} {...base}>
      <textarea
        id={id}
        className="input"
        rows={rows}
        spellCheck={spellCheck}
        value={value}
        disabled={base.disabled}
        aria-invalid={base.error ? true : undefined}
        aria-describedby={described(id, base.error, base.hint)}
        onChange={(e) => onChange(e.target.value)}
      />
    </Wrap>
  );
}

export function CheckField({
  label,
  checked,
  onChange,
  disabled,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: (v: boolean) => void;
  readonly disabled?: boolean | undefined;
}): ReactElement {
  return (
    <label className="body row check">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
      />
      {label}
    </label>
  );
}

/** Issues of a step in plain words, as one alert list. */
export function IssueList({
  issues,
  title,
}: {
  readonly issues: ReadonlyArray<{ readonly field: string; readonly message: string }>;
  readonly title: string;
}): ReactElement | null {
  if (issues.length === 0) return null;
  return (
    <div className="notice notice--fail" role="alert">
      <p className="label">{title}</p>
      <ul className="issues">
        {issues.map((i) => (
          <li key={`${i.field}:${i.message}`} className="body">
            {i.message}
          </li>
        ))}
      </ul>
    </div>
  );
}
